import { gql } from "@apollo/client";

const MANAGED_SUPPORT_REQUEST_FIELDS = gql`
    fragment ManagedSupportRequestFields on ManagedSupportRequest {
        id
        requestedAgentSeats
        requestedSupervisorSeats
        status
        provider
        meetingAt
        meetingNotes
        adminNotes
        provisionedAt
        createdAt
        updatedAt
        organization {
            id
            name
            domain
        }
        requestedBy {
            id
            email
            firstName
            lastName
        }
    }
`;

export const GET_MY_MANAGED_SUPPORT = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    query MyManagedSupport($organizationId: ID!) {
        myManagedSupport(organizationId: $organizationId) {
            config {
                agentMonthlyCents
                supervisorMonthlyCents
                currency
                provider
                configured
                shopifySubscriptionRequired
                shopifyPlanSelectionUrl
            }
            entitlement {
                status
                agentSeats
                supervisorSeats
                currentPeriodEnd
                cancelAtPeriodEnd
                kickoffMeetingAt
                assignedStaff {
                    seatType
                    assignedAt
                    user {
                        id
                        email
                        firstName
                        lastName
                    }
                }
            }
            latestRequest {
                ...ManagedSupportRequestFields
            }
        }
    }
`;

export const GET_MANAGED_SUPPORT_CONFIG = gql`
    query ManagedSupportConfig($organizationId: ID) {
        managedSupportConfig(organizationId: $organizationId) {
            agentMonthlyCents
            supervisorMonthlyCents
            currency
            provider
            configured
            shopifySubscriptionRequired
            shopifyPlanSelectionUrl
        }
    }
`;

export const CREATE_MANAGED_SUPPORT_REQUEST = gql`
    mutation CreateManagedSupportRequest($input: CreateManagedSupportRequestInput!) {
        createManagedSupportRequest(input: $input) {
            url
            sessionId
            requestId
        }
    }
`;

export const CONFIRM_MANAGED_SUPPORT_CHECKOUT = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation ConfirmManagedSupportCheckout($input: ConfirmManagedSupportCheckoutInput!) {
        confirmManagedSupportCheckout(input: $input) {
            config {
                agentMonthlyCents
                supervisorMonthlyCents
                currency
                provider
                configured
            }
            entitlement {
                status
                agentSeats
                supervisorSeats
                currentPeriodEnd
                cancelAtPeriodEnd
                kickoffMeetingAt
                assignedStaff {
                    seatType
                    assignedAt
                    user {
                        id
                        email
                        firstName
                        lastName
                    }
                }
            }
            latestRequest {
                ...ManagedSupportRequestFields
            }
        }
    }
`;

export const SCHEDULE_MANAGED_SUPPORT_MEETING = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation ScheduleManagedSupportMeeting($input: ScheduleManagedSupportMeetingInput!) {
        scheduleManagedSupportMeeting(input: $input) {
            ...ManagedSupportRequestFields
        }
    }
`;

export const CANCEL_MANAGED_SUPPORT_REQUEST = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation CancelManagedSupportRequest($organizationId: ID!) {
        cancelManagedSupportRequest(organizationId: $organizationId) {
            ...ManagedSupportRequestFields
        }
    }
`;

export const CANCEL_MANAGED_SUPPORT_SUBSCRIPTION = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation CancelManagedSupportSubscription($organizationId: ID!) {
        cancelManagedSupportSubscription(organizationId: $organizationId) {
            config {
                agentMonthlyCents
                supervisorMonthlyCents
                currency
                provider
                configured
            }
            entitlement {
                status
                agentSeats
                supervisorSeats
                currentPeriodEnd
                cancelAtPeriodEnd
                kickoffMeetingAt
                assignedStaff {
                    seatType
                    assignedAt
                    user {
                        id
                        email
                        firstName
                        lastName
                    }
                }
            }
            latestRequest {
                ...ManagedSupportRequestFields
            }
        }
    }
`;

export const RESUME_MANAGED_SUPPORT_SUBSCRIPTION = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation ResumeManagedSupportSubscription($organizationId: ID!) {
        resumeManagedSupportSubscription(organizationId: $organizationId) {
            config {
                agentMonthlyCents
                supervisorMonthlyCents
                currency
                provider
                configured
            }
            entitlement {
                status
                agentSeats
                supervisorSeats
                currentPeriodEnd
                cancelAtPeriodEnd
                kickoffMeetingAt
                assignedStaff {
                    seatType
                    assignedAt
                    user {
                        id
                        email
                        firstName
                        lastName
                    }
                }
            }
            latestRequest {
                ...ManagedSupportRequestFields
            }
        }
    }
`;

export const ADMIN_MANAGED_SUPPORT_REQUESTS = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    query AdminManagedSupportRequests($status: String, $page: Int, $limit: Int) {
        adminManagedSupportRequests(status: $status, page: $page, limit: $limit) {
            requests {
                ...ManagedSupportRequestFields
                subscriptionCancelAtPeriodEnd
            }
            total
            page
            totalPages
        }
    }
`;

export const ADMIN_MANAGED_SUPPORT_STAFF = gql`
    query AdminManagedSupportStaff {
        adminManagedSupportStaff {
            id
            email
            firstName
            lastName
            managedSupportRole
            isActive
            createdAt
            organizations {
                id
                name
                domain
            }
        }
    }
`;

export const ADMIN_CREATE_MANAGED_SUPPORT_STAFF = gql`
    mutation AdminCreateManagedSupportStaff($input: CreateManagedSupportStaffInput!) {
        adminCreateManagedSupportStaff(input: $input) {
            id
            email
            firstName
            lastName
            managedSupportRole
            isActive
            organizations {
                id
                name
            }
        }
    }
`;

export const ADMIN_ASSIGN_MANAGED_SUPPORT_STAFF = gql`
    mutation AdminAssignManagedSupportStaff($staffUserId: ID!, $organizationId: ID!) {
        adminAssignManagedSupportStaff(staffUserId: $staffUserId, organizationId: $organizationId) {
            id
            organizations {
                id
                name
            }
        }
    }
`;

export const ADMIN_UNASSIGN_MANAGED_SUPPORT_STAFF = gql`
    mutation AdminUnassignManagedSupportStaff($staffUserId: ID!, $organizationId: ID!) {
        adminUnassignManagedSupportStaff(staffUserId: $staffUserId, organizationId: $organizationId)
    }
`;

export const ADMIN_UPDATE_MANAGED_SUPPORT_REQUEST = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation AdminUpdateManagedSupportRequest($id: ID!, $input: UpdateManagedSupportRequestInput!) {
        adminUpdateManagedSupportRequest(id: $id, input: $input) {
            ...ManagedSupportRequestFields
        }
    }
`;

export const ADMIN_CANCEL_MANAGED_SUPPORT_REQUEST = gql`
    ${MANAGED_SUPPORT_REQUEST_FIELDS}
    mutation AdminCancelManagedSupportRequest($id: ID!) {
        adminCancelManagedSupportRequest(id: $id) {
            ...ManagedSupportRequestFields
            subscriptionCancelAtPeriodEnd
        }
    }
`;

export const ADMIN_CANCEL_MANAGED_SUPPORT_SUBSCRIPTION = gql`
    mutation AdminCancelManagedSupportSubscription($organizationId: ID!) {
        adminCancelManagedSupportSubscription(organizationId: $organizationId)
    }
`;

export const ADMIN_RESUME_MANAGED_SUPPORT_SUBSCRIPTION = gql`
    mutation AdminResumeManagedSupportSubscription($organizationId: ID!) {
        adminResumeManagedSupportSubscription(organizationId: $organizationId)
    }
`;
